import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { apiErrorMessage } from "../../../utility/lib/error";
import { Snackbar } from "../../common/VFX-UI";

import { FileDto } from "../../../app/model/api/types/dto";
import reportApi from "../../../app/model/api/reportApi";
import organizationApi from "../../../app/model/api/organizationApi";
import Import from "./Import";
import { EMPTY_GUID } from "../../../utility/constants";
import IncorrectEmployeeData from "./IncorrectEmployeeData";
import { queryKeys } from "./constants";

const ImportContainer = () => {
  const [snackbar, setSnackbar] = useState({
    showSnackbar: false,
    message: "",
  });

  const [isUploadSuccess, setIsUploadSuccess] = useState<null | boolean>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const exportUrl = reportApi.exportInvalidEmployeesData();

  const onShowSnackbar = (message: string) => {
    setSnackbar({
      showSnackbar: true,
      message: message,
    });
  };

  const onCloseSnackbar = () => {
    setSnackbar({
      showSnackbar: false,
      message: "",
    });
    setIsUploadSuccess(null);
  };

  const {isLoading, isPending, data, refetch} = useQuery({
    queryKey: [queryKeys.getInvalidEmployee],
    queryFn: () => organizationApi.getFailedEmployeeUploadRequest(),
    select: (data) => {
      const result = data.result.map((x)=>{return {...x.employee, reason:x.reason}});
      return result;
    },
  });

  const { status, mutate } = useMutation<void, Error, FileDto | null>({
    mutationFn: async (payload: FileDto | null) => {
      let errorMessage = null;
      if (payload) {
        const response = await organizationApi.UploadEmployees(payload);
        if (response?.isSuccess) {
          setIsUploadSuccess(true);
          onShowSnackbar("Upload Submitted Successfully");
        } else {
          errorMessage = apiErrorMessage(response.errorMessages);
          refetch();
          if (errorMessage) {
            setIsUploadSuccess(false);
            onShowSnackbar(errorMessage);
          }
        }
      } else {
        setIsUploadSuccess(false);
        errorMessage = "Unable to upload.";
        onShowSnackbar(errorMessage);
      }

      // Reset the value of the file input to clear the selection
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setUploadFile(null);
    },
  });

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async () => {
    let fileDto: FileDto | null = null;
    if (uploadFile) {
      const base64Content = await convertFileToBase64(uploadFile);
      fileDto = {
        id: EMPTY_GUID,
        fileName: uploadFile.name,
        mimeType: "application/vnd.ms-excel", //file.type,
        content: base64Content.split(",")[1],
      };
    }
    mutate(fileDto);
  };

  return (
    <>
      <Import
        uploadFile={uploadFile}
        setUploadFile={setUploadFile}
        exportUrl={exportUrl}
        status={status}
        handleUpload={handleUpload}
        fileInputRef={fileInputRef}
      />

      <div style={{marginBottom:"5rem"}}/>

      {!isLoading && !isPending && data !== undefined && data.length > 0 &&
        <IncorrectEmployeeData data={data} />
      }
      
      {isUploadSuccess !== null && (
        <Snackbar
          open={true}
          message={snackbar.message}
          handleClose={onCloseSnackbar}
          severity={isUploadSuccess ? "success" : "danger"}
        />
      )}
    </>
  );
};

export default ImportContainer;
